import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { artistAdminApi, type GetArtistsParams, type SendEmailPayload } from '../api/artistAdminApi'
import { useToast } from '@/hooks/use-toast'
import type { EmailPreferences, CreativeGenome } from '../types'

export function useArtists(params: GetArtistsParams = {}) {
  return useQuery({
    queryKey: ['artists', params],
    queryFn: () => artistAdminApi.getArtists(params),
  })
}

export function useArtist(artistId: number | null) {
  return useQuery({
    queryKey: ['artist', artistId],
    queryFn: () => artistAdminApi.getArtist(artistId!),
    enabled: !!artistId,
  })
}

export function useArtistCommunications(
  artistId: number | null,
  params: { limit?: number; offset?: number; emailType?: string } = {}
) {
  return useQuery({
    queryKey: ['artistCommunications', artistId, params],
    queryFn: () => artistAdminApi.getArtistCommunications(artistId!, params),
    enabled: !!artistId,
  })
}

export function useSendEmailToArtist() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ artistId, payload }: { artistId: number; payload: SendEmailPayload }) =>
      artistAdminApi.sendEmailToArtist(artistId, payload),
    onSuccess: (data, { artistId }) => {
      queryClient.invalidateQueries({ queryKey: ['artistCommunications', artistId] })
      queryClient.invalidateQueries({ queryKey: ['artist', artistId] })
      toast({
        title: 'Email sent',
        description: data.message,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to send email',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

export function useArtistGenome(artistId: number | null) {
  return useQuery({
    queryKey: ['artistGenome', artistId],
    queryFn: () => artistAdminApi.getArtistGenome(artistId!),
    enabled: !!artistId,
  })
}

export function useUpdateArtistGenome() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ artistId, genome }: { artistId: number; genome: CreativeGenome }) =>
      artistAdminApi.updateArtistGenome(artistId, genome),
    onSuccess: (_, { artistId }) => {
      queryClient.invalidateQueries({ queryKey: ['artistGenome', artistId] })
      toast({
        title: 'Creative Genome saved',
        description: 'Changes will flow to the artist’s generated experiences.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to save genome',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

export function useUpdateArtistEmailPreferences() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ artistId, preferences }: { artistId: number; preferences: Partial<EmailPreferences> }) =>
      artistAdminApi.updateArtistEmailPreferences(artistId, preferences),
    onSuccess: (_, { artistId }) => {
      queryClient.invalidateQueries({ queryKey: ['artist', artistId] })
      toast({
        title: 'Preferences updated',
        description: 'Email preferences have been saved.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update preferences',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}
